/**
 * **API playground** — `/developers/playground`
 * (`specs/M8-P4-developers-docs-playground.md` §7).
 *
 * GraphiQL 4, bundled from npm. Not from a CDN: the deployment's CSP allows no third-party
 * script, and a playground that only works when `unpkg.com` is reachable is not a playground.
 * It is loaded with `React.lazy`, so the ~1 MB editor is a chunk the other 40 screens of this
 * app never download.
 *
 * **Two credentials, because `/v2` is token-only by contract** (M0-F3 acceptance A10 — a session
 * cookie sent to `/v2` is ignored, deliberately, exactly as on monday):
 *
 * - *Your session* (the default) runs against `/v2` with a short-lived credential minted by
 *   `developers.playgroundToken` for this session. It lives an hour, is rotated while the tab is
 *   open, is revoked when the tab is closed or the session ends, is never listed on the *API
 *   token* page, and is held in this component's memory — never in `localStorage`, never in the
 *   URL, never in GraphiQL's own header editor.
 * - *A personal token of yours*, by name. Its value never reaches the browser: the request goes
 *   to `POST /developers/v2-proxy?tokenId=…` with the session, and the api forwards it to `/v2`
 *   with that token, handing every response header back.
 *
 * Mutations run for real, as they do on monday. The first one in a session asks first, because
 * the person is one click from writing to a production board.
 */
import { Button } from '@elizmonday/ui';
import { useQuery } from '@tanstack/react-query';
import type * as React from 'react';
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { DeveloperShell } from '../developer';
import { usePreference } from '../shell/preferences';
import { CLIENT_HEADER_NAME } from './constants';
import {
  fillPlaceholders,
  idsFromLookup,
  type DevelopersSource,
  type PlaygroundPlaceholder,
} from './source';
import { useDevelopersSource } from './chrome';
import { useTrpcDevelopersSource } from './trpc-source';
import './developers.css';

/** The editor, and its stylesheet, in one lazily-loaded chunk. */
const GraphiQLLazy = lazy(async () => {
  const [{ GraphiQL }] = await Promise.all([import('graphiql'), import('graphiql/style.css')]);
  return { default: GraphiQL as unknown as React.ComponentType<Record<string, unknown>> };
});

interface FetcherParams {
  query: string;
  variables?: Record<string, unknown>;
  operationName?: string | null;
}

/** What the response pane's footer prints: the budget counters `/v2` really returned. */
interface LastResponse {
  status: number;
  durationMs: number;
  headers: Record<string, string>;
}

const INTERESTING_HEADERS = ['api-version', 'ratelimit', 'ratelimit-policy', 'retry-after'];

/** A document that writes. Cheap and deliberate: the confirmation is about intent, not parsing. */
const isMutation = (query: string): boolean => /(^|\s)mutation(\s|\{)/.test(query);

export interface PlaygroundPageProps {
  source?: DevelopersSource;
  /** Tests render the chrome without pulling a 1 MB editor into jsdom. */
  renderEditor?: boolean;
}

export function PlaygroundPage({
  source: injected,
  renderEditor = true,
}: PlaygroundPageProps = {}): React.ReactElement {
  const trpcSource = useTrpcDevelopersSource();
  const source = useDevelopersSource(injected, trpcSource);
  const [searchParams] = useSearchParams();
  const [theme] = usePreference('shell.theme');

  const setup = useQuery({ queryKey: ['developers', 'playground'], queryFn: () => source.playground() });

  const [principal, setPrincipal] = useState<string>('session');
  const [version, setVersion] = useState<string | null>(null);
  const [ids, setIds] = useState<Partial<Record<PlaygroundPlaceholder, string | null>>>({});
  const [last, setLast] = useState<LastResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mutationsConfirmed, setMutationsConfirmed] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(false);
  /** The credential. In a ref, so it never lands in a render tree, a log or the React devtools. */
  const credential = useRef<string | null>(null);
  const [document, setDocument] = useState<string>(
    () => searchParams.get('document') ?? 'query {\n  me {\n    id\n    name\n  }\n}',
  );

  const resolvedVersion = version ?? setup.data?.defaultVersion ?? '2026-10';
  const ttlMs = (setup.data?.credentialTtlSeconds ?? 3600) * 1000;

  /** Mint on arrival, rotate at four fifths of the hour, hand it back when the tab closes. */
  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const mint = async (): Promise<void> => {
      try {
        const minted = await source.mintCredential();
        if (cancelled) return;
        credential.current = minted.value;
        timer = window.setTimeout(() => void mint(), Math.max(60_000, ttlMs * 0.8));
      } catch (mintError) {
        if (!cancelled) setError((mintError as Error).message);
      }
    };
    void mint();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      credential.current = null;
      void source.releaseCredential().catch(() => undefined);
    };
  }, [source, ttlMs]);

  /**
   * One request. Either straight to `/v2` with the session credential, or through the proxy
   * when a named token is selected — the two paths of §7, and nothing in between.
   */
  const send = useCallback(
    async (params: FetcherParams): Promise<unknown> => {
      const named = principal !== 'session';
      const endpoint = named
        ? `/developers/v2-proxy?tokenId=${encodeURIComponent(principal)}`
        : (setup.data?.endpoint ?? '/v2');

      const headers: Record<string, string> = {
        'content-type': 'application/json',
        'api-version': resolvedVersion,
      };
      if (named) {
        // The proxy is a browser endpoint behind the session, so it carries the CSRF contract.
        headers[CLIENT_HEADER_NAME] = 'web';
      } else {
        const value = credential.current;
        if (!value) throw new Error('The playground credential is not ready yet. Try again in a moment.');
        headers.authorization = value;
      }

      const startedAt = performance.now();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        credentials: named ? 'same-origin' : 'omit',
        body: JSON.stringify(params),
      });
      const collected: Record<string, string> = {};
      for (const name of INTERESTING_HEADERS) {
        const value = response.headers.get(name);
        if (value !== null) collected[name] = value;
      }
      setLast({
        status: response.status,
        durationMs: Math.round(performance.now() - startedAt),
        headers: collected,
      });

      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch {
        return { errors: [{ message: text.slice(0, 2000) }] };
      }
    },
    [principal, resolvedVersion, setup.data?.endpoint],
  );

  const fetcher = useCallback(
    async (params: FetcherParams): Promise<unknown> => {
      setError(null);
      if (isMutation(params.query) && !mutationsConfirmed) {
        setPendingConfirm(true);
        return {
          errors: [
            {
              message:
                'This document writes. Mutations in the playground run for real, exactly as on ' +
                'monday — confirm above, then run it again.',
            },
          ],
        };
      }
      try {
        return await send(params);
      } catch (sendError) {
        const message = (sendError as Error).message;
        setError(message);
        return { errors: [{ message }] };
      }
    },
    [mutationsConfirmed, send],
  );

  /** Fill the examples' `{{boardId}}` placeholders with ids this person can actually read. */
  useEffect(() => {
    const lookup = setup.data?.idLookup;
    // No editor, nothing to fill: the harness renders the chrome and must make no request.
    if (!lookup || !renderEditor) return;
    let cancelled = false;
    const run = async (): Promise<void> => {
      // Give the credential a moment to land; a failure here only costs the placeholders.
      await new Promise((resolve) => window.setTimeout(resolve, 150));
      if (cancelled || credential.current === null) return;
      try {
        const body = (await send({ query: lookup })) as { data?: unknown };
        if (!cancelled) setIds(idsFromLookup(body?.data));
      } catch {
        /* keep the placeholders */
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [renderEditor, send, setup.data?.idLookup]);

  const examples = useMemo(
    () =>
      (setup.data?.examples ?? []).map((example) => ({
        ...example,
        filled: fillPlaceholders(example.document, ids),
      })),
    [setup.data?.examples, ids],
  );

  const namedToken = setup.data?.tokens.find((token) => token.id === principal);

  return (
    <DeveloperShell current="playground">
      <div className="ez-play" data-testid="developers-playground">
        <h1 className="ez-dev__heading">API playground</h1>
        <p className="ez-dev__lede">
          Runs against the real <code>{setup.data?.endpoint ?? '/v2'}</code> with your own rights.
          Mutations are not simulated.
        </p>

        <div className="ez-play__bar">
          <label>
            Run as
            <select
              aria-label="Run as"
              data-testid="playground-principal"
              value={principal}
              onChange={(event) => setPrincipal(event.target.value)}
            >
              <option value="session">Your session</option>
              {(setup.data?.tokens ?? []).map((token) => (
                <option key={token.id} value={token.id}>
                  {token.name}
                  {token.replayable ? '' : ' (value not stored)'}
                </option>
              ))}
            </select>
          </label>
          <label>
            API-Version
            <select
              aria-label="API-Version"
              data-testid="playground-version"
              value={resolvedVersion}
              onChange={(event) => setVersion(event.target.value)}
            >
              {(setup.data?.versions ?? []).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          {namedToken && !namedToken.replayable ? (
            <span className="ez-docs__meta" data-testid="playground-not-replayable">
              This deployment keeps no copy of that token’s value, so the run will be refused.
              Regenerate it, or run with your session.
            </span>
          ) : null}
        </div>

        {pendingConfirm && !mutationsConfirmed ? (
          <div className="ez-dev__note ez-dev__note--warning" data-testid="playground-confirm">
            <span>
              That document <strong>writes</strong>. It will change real data on a real board, the
              same as monday’s own playground.
            </span>
            <Button
              kind="primary"
              size="small"
              data-testid="playground-confirm-accept"
              onClick={() => {
                setMutationsConfirmed(true);
                setPendingConfirm(false);
              }}
            >
              I understand, allow mutations
            </Button>
            <Button kind="tertiary" size="small" onClick={() => setPendingConfirm(false)}>
              Cancel
            </Button>
          </div>
        ) : null}

        {examples.length > 0 ? (
          <div className="ez-play__examples" data-testid="playground-examples">
            {examples.map((example) => (
              <button
                key={example.id}
                type="button"
                className="ez-play__example"
                data-kind={example.kind}
                data-testid={`playground-example-${example.id}`}
                title={example.description}
                onClick={() => setDocument(example.filled)}
              >
                {example.title}
                {example.kind === 'mutation' ? ' ✎' : ''}
              </button>
            ))}
          </div>
        ) : null}

        {/* M0-F9: the editor needs a keyboard and two panes. Say so, then let them try anyway. */}
        <p className="ez-docs__meta" data-testid="playground-phone-notice">
          The playground is best on a desktop screen — it is a two-pane code editor. It works
          here, but the panes stack.
        </p>

        {error ? (
          <p className="ez-dev__error" data-testid="playground-error">
            {error}
          </p>
        ) : null}

        <div className="ez-play__frame" data-testid="playground-frame">
          {renderEditor ? (
            <Suspense fallback={<p className="ez-docs__prose">Loading the editor…</p>}>
              <GraphiQLLazy
                fetcher={fetcher}
                query={document}
                onEditQuery={(value: string) => setDocument(value)}
                forcedTheme={theme === 'light' ? 'light' : 'dark'}
                isHeadersEditorEnabled={false}
                defaultEditorToolsVisibility="variables"
              />
            </Suspense>
          ) : null}
        </div>

        {last ? (
          <p className="ez-play__headers" data-testid="playground-response-headers">
            {`HTTP ${last.status} · ${last.durationMs} ms\n`}
            {Object.entries(last.headers)
              .map(([key, value]) => `${key}: ${value}`)
              .join('\n')}
          </p>
        ) : null}
      </div>
    </DeveloperShell>
  );
}
